import React, { FC, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import ConfirmDialog from '@components/Dialogs/ConfirmDialog';
import CriteriaBlock from '@components/EntityDrawers/components/CiaInputsGroup/CriteriaBlock';
import useCiaData from '@components/EntityDrawers/components/CiaInputsGroup/hooks/useCiaData';
import useStore from '@components/MainStage/store';
import { Modes } from '@constants/canvas/layers';
import { ISelectOption } from '@constants/entities/ui';
import { useToast } from '@context/Toast/ToastProvider';
import useAssets from '@hooks/useAssets';
import useProjectId from '@hooks/useProjectId';
import useSaveEntitiesResponse from '@hooks/useSaveEntitiesResponse';
import { Box } from '@mui/material';
import { useUpdateNodesActionMutation } from '@store/services/nodes';
import {
  INodeActionOutcome,
  NodeActionResult,
  NodeActionType,
  NodeDTO,
} from '@store/services/nodes/types';
import { transformCriteriaObjectToArray } from '@utils/drawers';
import { NODE_ACTIONS } from '@utils/nodeActions';

import { NodeActionConfirmModal } from './NodeActionConfirmModal';

export type CiaMenuActionType =
  | NodeActionType.AddCoverage
  | NodeActionType.AddAssurance
  | NodeActionType.AddApplicability;

type Props = {
  open: boolean;
  onClose: () => void;
  actionType: CiaMenuActionType;
  nodeIds: string[];
  mode: Modes;
};

export const AddCiaModal: FC<Props> = ({
  open,
  onClose,
  actionType,
  nodeIds,
  mode,
}) => {
  const { getNodeById } = useStore();
  const { questionnaire } = useCiaData();
  const { selectedAssetId } = useAssets();
  const projectId = useProjectId(true);
  const { showToast } = useToast();
  const { saveResponseToRedux } = useSaveEntitiesResponse();
  const [updateNodesAction, { isLoading }] = useUpdateNodesActionMutation();
  const [confirming, setConfirming] = useState(false);
  const [outcomes, setOutcomes] = useState<INodeActionOutcome[]>([]);

  const config = NODE_ACTIONS[actionType]!;
  const dimension = config.ciaDimension!;
  const criterias = questionnaire?.cia_criteria?.[dimension]?.criterias ?? [];

  const methods = useForm({ defaultValues: { [dimension]: {} } });

  useEffect(() => {
    if (open) methods.reset({ [dimension]: {} });
  }, [open, dimension, methods]);

  const values = methods.watch(dimension) as
    | Record<string, ISelectOption[] | null>
    | undefined;
  const hasSelection = Object.values(values ?? {}).some(
    (value) => Array.isArray(value) && value.length > 0,
  );

  const toItem = (outcome: INodeActionOutcome) => ({
    id: outcome.node_id,
    name:
      (getNodeById(outcome.node_id)?.data?.dto as NodeDTO | undefined)?.name ??
      outcome.node_id,
  });

  const close = () => {
    setConfirming(false);
    setOutcomes([]);
    onClose();
  };

  const run = async (force: boolean) => {
    const picked = transformCriteriaObjectToArray(
      (methods.getValues(dimension) ?? {}) as Record<string, ISelectOption[]>,
    ).filter(({ answers }) => answers.length > 0);

    try {
      const response = await updateNodesAction({
        nodeIds,
        actionType,
        payload: { criterias: picked },
        mode,
        assetId: selectedAssetId || undefined,
        force,
        projectId,
      }).unwrap();

      if (!response.applied) {
        setOutcomes(response.outcomes);
        setConfirming(true);
        return;
      }

      saveResponseToRedux(response);
      showToast(config.successMessage, 'success');
    } catch {
      showToast('Something went wrong. Please try again.', 'error');
    }

    close();
  };

  return (
    <>
      <ConfirmDialog
        open={open && !confirming}
        onClose={close}
        onBack={close}
        onConfirm={() => run(false)}
        disabled={!hasSelection || isLoading}
        title={config.label}
        confirmName={config.menuConfirmName}
        backName="Cancel"
      >
        <FormProvider {...methods}>
          <Box
            sx={{
              maxHeight: 360,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
            }}
          >
            <CriteriaBlock
              title=""
              fields={criterias}
              criteriaKey={dimension}
              revealOnMenuClose
            />
          </Box>
        </FormProvider>
      </ConfirmDialog>

      <NodeActionConfirmModal
        open={open && confirming}
        title={config.menuLabel}
        text={config.menuText}
        toChange={outcomes
          .filter(
            (o) =>
              o.result === NodeActionResult.Updated ||
              o.result === NodeActionResult.Skipped,
          )
          .map(toItem)}
        toChangeTitle={config.menuToChangeTitle}
        skipped={outcomes
          .filter(
            (o) =>
              o.result === NodeActionResult.Ineligible ||
              o.result === NodeActionResult.Blocked,
          )
          .map(toItem)}
        toSkipTitle={config.menuToSkipTitle}
        confirmName={config.menuConfirmName}
        onBack={close}
        onConfirm={() => {
          if (!isLoading) run(true);
        }}
      />
    </>
  );
};
